

import { Component, Vue, Watch } from "vue-property-decorator"

import VuePerfectScrollbar from "vue-perfect-scrollbar"

import { Format, FormatMargin, FormatStyle } from "@/types/format"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"
import RichTextTemplate from "@/components/Controls/RichTextTemplate.vue"

import formatApi from "@/services/api/Formatting"
import { Field, Template } from "@/types"
import { namespace } from "vuex-class"
import api from "@/services/api"
import Placeholder from "@/utils/Placeholder"

const setup = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"skeleton": SkeletonLoader
	}
})
export default class FormatSetup extends Vue {
    @setup.State fields!: Field[];

	private caseNumber: string | null = null
	private loading = false
	private downloading = false
	private currentTemplateId: string | null = "";
	private currentCaseHeaderTemplate: string | null = "";
	private currentCaseFooterTemplate: string | null = "";
	private currentFormat: Format | null = null;

	private formats: Format[] = []
	public templates: Template[] = []

	private get columns () {
		return [
			{
				key: "id",
				inTable: false,
				inModal: () => false
			},
			{
				key: "templateId",
				label: "Template",
				sortable: true,
				dataType: "list",
				options: this.templates,
				hasTemplate: true,
				linkedColumn: { columnKey: "formatTemplate", prop: "templateId" },
				valueColumn: "templateId",
				required: true
			},
			{
				key: "title",
				label: "Title"
			},
			{
				key: "formatTemplate",
				label: "Format",
				inTable: false,
				dynamicComponent: RichTextTemplate,
				componentProperties: {
					"additionalFields": [
						{ id: "caseLink", label: "Link to Record" },
						{ id: "event.originatingId", label: "Originating ID" },
						{ id: "event.keyMedia", label: "Key Media" },
						{ id: "event.originatingEvent", label: "Originating Event" }
					],
					templateId: this.currentTemplateId,
					caseHeader: this.currentCaseHeaderTemplate,
					formatFooter: this.currentCaseFooterTemplate,
					formatMargin: this.currentFormat?.margin,
					formatStyles: this.currentFormat?.cssOverride
				},
				eventHandlers: {
					setIncludeCaseHeader: this.setIncludeCaseHeader,
					setUsePageNumbers: this.setUsePageNumbers,
					setFormatMargin: this.setFormatMargin,
					setCssOverrides: this.setCssOverrides
				}
			}
		]
	}

	async mounted () {
		this.loading = true
		this.formats = await formatApi.formatGet()
		this.templates = await api.categories()
		this.loading = false
	}

	async saveFormat (format: Format) {
		let formatExists = !!format.id

		format.headerTemplate = this.currentFormat?.headerTemplate || null
		format.footerTemplate = this.currentFormat?.footerTemplate || null
		format.margin = this.currentFormat?.margin || null
		format.cssOverride = this.currentFormat?.cssOverride || null

		let newFormat = await formatApi.formatSave(format)
		if (formatExists) {
			const existingIndex = this.formats.findIndex(f => f.id === newFormat.id)
			if (existingIndex) {
				Vue.set(this.formats, existingIndex, newFormat)
			}
		} else {
			this.formats.push(newFormat)
		}
	}

	async deleteFormat (format: Format) {
		if (format.id == null) {
			return
		}

		const successfullyDeleted = await formatApi.formatDelete(format.id)
		if (successfullyDeleted) {
			this.formats = this.formats.filter(f => f.id !== format.id)
		}
	}

	async download (item: Format) {
		if (!this.caseNumber) return

		item.headerTemplate = this.currentFormat?.headerTemplate || null
		item.footerTemplate = this.currentFormat?.footerTemplate || null
		item.margin = this.currentFormat?.margin || null
		item.cssOverride = this.currentFormat?.cssOverride || null

		this.downloading = true
		try {
			await formatApi.downloadPdf(item.title, this.caseNumber!, item)
		} finally {
			this.downloading = false
		}
	}

	private getTemplateTitle (templateId: string) {
		return this.templates.find(template => template.id === templateId)?.title ?? ""
	}

	private setIncludeCaseHeader (includeCaseHeader: boolean) {
		var item = this.currentFormat

		if (item == null) {
			return
		}

		if (includeCaseHeader) {
			item.headerTemplate = `<div class="ql-align-center predefined-header">
															Record Number: ${Placeholder.getPlaceholderHtml("caseNumber", "Case #")} -
															${Placeholder.getPlaceholderHtml("typeOfIncidentOrTitle", "Type of Incident or Case Title")} -
															Status: ${Placeholder.getPlaceholderHtml("statusTitle", "Status")}
														</div>`
		} else {
			item.headerTemplate = null
		}

		this.currentFormat = item
	}

	private setUsePageNumbers (usePageNumber: boolean) {
		if (this.currentFormat == null) {
			return
		}

		if (usePageNumber) {
			this.currentFormat.footerTemplate =
				`<div class="ql-align-right predefined-footer">
					Date/Time Printed: ${Placeholder.getPlaceholderHtml("currentDateTime", "Current Date and Time")} | {page} of {total-pages}
				</div>`
		} else {
			this.currentFormat.footerTemplate = null
		}
	}

	private formatClick (format: Format) {
		this.currentFormat = format
		this.currentTemplateId = format.templateId
		this.currentCaseHeaderTemplate = format.headerTemplate
		this.currentCaseFooterTemplate = format.footerTemplate
	}

	private createNewFormat () {
		var format: Format = {
			id: null,
			templateId: "",
			title: "",
			formatTemplate: "",
			headerTemplate: null,
			footerTemplate: null,
			margin: null,
			cssOverride: null
		}

		this.currentFormat = format
		return format
	}

	private setFormatMargin (formatMargin: FormatMargin): void {
		if (this.currentFormat != null) {
			this.currentFormat.margin = formatMargin
		}
	}

	private setCssOverrides (cssOverride: FormatStyle): void {
		if (this.currentFormat != null) {
			this.currentFormat.cssOverride = cssOverride
		}
	}
}

